import { Deferred, Head, Link, router, usePage } from "@inertiajs/react"
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyData = Record<string, any>
import AddIcon from "@mui/icons-material/Add"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import CloseIcon from "@mui/icons-material/Close"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined"
import DownloadIcon from "@mui/icons-material/Download"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp"
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    Container,
    Divider,
    FormControlLabel,
    IconButton,
    Paper,
    Skeleton,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers"
import dayjs from "dayjs"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import {
    Control,
    Controller,
    SubmitHandler,
    useFieldArray,
    useForm,
    useWatch,
} from "react-hook-form"
import { formatErrorMessages, translateErrorMessage } from "@/lib/formErrors"
import { formatMoney } from "@/lib/format"
import { calcDayTotal, computeEntryTotalDeductions, getHoursSalary, type ISlimReport } from "@/lib/payrollTotals"
import { parseDateOnly } from "@/lib/time"
import Util from "@/lib/util"
import { IEmployee } from "@/models/employee"
import { IGeneralInfo, IGeneralInfoEntryAdditionalRetention } from "@/models/generalInfo"
import {
    IPayroll,
    IPayrollEntry,
    IPayrollEntryDay,
    IPayrollEntryDayAdditionalRetention,
    Payroll,
    PayrollEntry,
    PayrollEntryDay,
    PayrollEntryDayAdditionalRetention,
} from "@/models/payroll"

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
    is_edit: boolean
    payroll_base?: { id: number; payroll_name: string; payroll_date: string } | null
    payroll?: IPayroll | null
    general_infos?: IGeneralInfo[]
    reports?: ISlimReport[]
    back_params: Record<string, string | null>
    errors?: Record<string, string[]>
}

// ─── LoanRetentionsAccordion ─────────────────────────────────────────────────

interface LoanRetentionsProps {
    control: Control<IPayroll>
    entryIndex: number
    dayIndex: number
    generalInfo: IGeneralInfo | null
    generalInfos: IGeneralInfo[]
    getValues: () => IPayroll
}

function LoanRetentionsAccordion({ control, entryIndex, dayIndex, generalInfo, generalInfos, getValues }: LoanRetentionsProps) {
    const { t } = useTranslation()
    const base = `payroll_entries.${entryIndex}.payroll_entry_days.${dayIndex}.payroll_entry_day_additional_retentions`

    const { fields, append, remove, update, replace, swap } = useFieldArray({
        control,
        name: base as unknown as "payroll_entries",
    })

    const employeeId = useWatch({ control, name: `payroll_entries.${entryIndex}.employee_id` as keyof IPayroll })

    const loanRetentions = generalInfo && employeeId
        ? Util.getEmployeeLoanRetentions(Number(employeeId), generalInfo.id, generalInfos)
        : []

    // Resync this day's loan retentions when the user changes the entry's
    // employee. Persisted rows are tombstoned (_destroy: true) so Rails
    // deletes them; ephemeral rows are dropped; then the new employee's
    // loan retentions are appended. The first effect run after mount only
    // records the initial id without resyncing — the loaded data is
    // already correct.
    const syncKey = `${Number(employeeId) || 0}|${generalInfo?.id ?? 0}`
    const prevSyncKey = useRef<string | null>(null)
    useEffect(() => {
        if (prevSyncKey.current === null) {
            prevSyncKey.current = syncKey
            return
        }
        if (prevSyncKey.current === syncKey) return
        prevSyncKey.current = syncKey

        const current = getValues()
            .payroll_entries[entryIndex]
            .payroll_entry_days[dayIndex]
            .payroll_entry_day_additional_retentions ?? []

        const tombstones = current
            .filter(r => r.id && r.id > 0)
            .map(r => ({ ...r, _destroy: true, is_active: false }))

        const fresh = generalInfo && Number(employeeId)
            ? Util.getEmployeeActiveLoanRetentions(Number(employeeId), generalInfo.id, generalInfos).map(r => {
                const ret = new PayrollEntryDayAdditionalRetention()
                ret.general_info_entry_additional_retention_id = r.id
                ret.general_info_entry_additional_retention = r
                ret.is_active = true
                ret._destroy = false
                ret.id = 0
                return ret
            })
            : []

        replace([...tombstones, ...fresh] as unknown as IPayrollEntry[])
    }, [syncKey])

    const handleAdd = () => append(new PayrollEntryDayAdditionalRetention() as unknown as IPayrollEntry)

    const handleRemove = (idx: number) => {
        const ret = getValues()
            .payroll_entries[entryIndex]
            .payroll_entry_days[dayIndex]
            .payroll_entry_day_additional_retentions[idx]
        if (ret?.id && ret.id > 0) {
            update(idx, { ...ret, _destroy: true } as unknown as IPayrollEntry)
        } else {
            remove(idx)
        }
    }

    const handleRetentionChange = (index: number, retention: { id: number; description: string; amount: number } | null) => {
        // getValues, not `fields`: the array snapshot is only refreshed by array
        // operations, so it still carries the pre-edit is_active value.
        const field = getValues()
            .payroll_entries[entryIndex]
            .payroll_entry_days[dayIndex]
            .payroll_entry_day_additional_retentions[index]
        if (retention) {
            update(index, {
                ...field,
                general_info_entry_additional_retention_id: retention.id,
                general_info_entry_additional_retention: retention,
                is_active: true,
            } as unknown as IPayrollEntry)
        } else {
            update(index, {
                ...field,
                general_info_entry_additional_retention_id: 0,
                general_info_entry_additional_retention: null,
            } as unknown as IPayrollEntry)
        }
    }

    const allFields = fields as unknown as IPayrollEntryDayAdditionalRetention[]
    const visibleIndices = allFields.flatMap((f, i) => f._destroy ? [] : [i])
    const visibleCount = visibleIndices.length

    const swapVisible = (idx: number, direction: -1 | 1) => {
        const here = visibleIndices.indexOf(idx)
        const target = visibleIndices[here + direction]
        if (target !== undefined) swap(idx, target)
    }

    return (
        <Accordion disableGutters variant="outlined" sx={{ mt: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 36, "& .MuiAccordionSummary-content": { my: 0.5 } }}>
                <Typography variant="body2">{t("payrolls.form.loanRetentions", { count: visibleCount })}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 1 }}>
                <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                    <Button size="small" startIcon={<AddIcon />} onClick={handleAdd}>{t("common.add")}</Button>
                </Box>
                {(fields as unknown as IPayrollEntryDayAdditionalRetention[]).map((field, idx) => {
                    if (field._destroy) return null
                    return (
                    // Composite key includes the parent indices so the row
                    // remounts (with fresh Controller subscriptions) when the
                    // entry or day is moved by a server-side reorder. Without
                    // this, the inner Controllers' `name` props change in
                    // place and useController fails to fully resync.
                    <Box key={`${entryIndex}-${dayIndex}-${field.id}-${idx}`} sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}>
                        <Controller
                            control={control}
                            name={`${base}.${idx}.is_active` as keyof IPayroll}
                            render={({ field: f }) => (
                                <Tooltip title={t("payrolls.form.active")}>
                                    <Checkbox size="small" checked={!!f.value} onChange={e => f.onChange(e.target.checked)} />
                                </Tooltip>
                            )}
                        />
                        <Controller
                            control={control}
                            name={`${base}.${idx}.general_info_entry_additional_retention` as keyof IPayroll}
                            render={({ field: f }) => (
                                <Autocomplete
                                    sx={{ flex: 1 }}
                                    size="small"
                                    options={loanRetentions}
                                    value={(f.value as { id: number, description: string, amount: number } | null) ?? null}
                                    isOptionEqualToValue={(op, v) => op.id === v.id}
                                    getOptionLabel={op => {
                                        const r = op as unknown as IGeneralInfoEntryAdditionalRetention
                                        const baseLabel = `${r.description} - RD$${formatMoney(r.amount)}`
                                        return r.is_active === false ? `${baseLabel}${t("payrolls.form.inactiveSuffix")}` : baseLabel
                                    }}
                                    onChange={(_, data) => handleRetentionChange(idx, data)}
                                    renderInput={params => <TextField {...params} label={t("payrolls.form.loanRetention")} />}
                                />
                            )}
                        />
                        <Tooltip title={t("payrolls.form.moveUp")}>
                            <span>
                                <IconButton
                                    size="small"
                                    disabled={visibleIndices[0] === idx}
                                    onClick={() => swapVisible(idx, -1)}
                                >
                                    <KeyboardArrowUpIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title={t("payrolls.form.moveDown")}>
                            <span>
                                <IconButton
                                    size="small"
                                    disabled={visibleIndices[visibleIndices.length - 1] === idx}
                                    onClick={() => swapVisible(idx, 1)}
                                >
                                    <KeyboardArrowDownIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title={t("common.remove")}>
                            <IconButton size="small" color="error" onClick={() => handleRemove(idx)}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    )
                })}
            </AccordionDetails>
        </Accordion>
    )
}

// ─── DayCard ─────────────────────────────────────────────────────────────────

interface DayCardProps {
    control: Control<IPayroll>
    entryIndex: number
    dayIndex: number
    generalInfo: IGeneralInfo | null
    generalInfos: IGeneralInfo[]
    reports: ISlimReport[]
    setValue: (name: string, value: unknown) => void
    getValues: () => IPayroll
    onRemove: () => void
}

function DayCard({ control, entryIndex, dayIndex, generalInfo, generalInfos, reports, setValue, getValues, onRemove }: DayCardProps) {
    const { t } = useTranslation()
    const base = `payroll_entries.${entryIndex}.payroll_entry_days.${dayIndex}`

    const employeeId = useWatch({ control, name: `payroll_entries.${entryIndex}.employee_id` as keyof IPayroll })
    const entryDate = useWatch({ control, name: `${base}.entry_date` as keyof IPayroll })
    const isCustom = useWatch({ control, name: `${base}.is_custom` as keyof IPayroll })
    const additional = useWatch({ control, name: `${base}.additional_value` as keyof IPayroll })
    const retentions = useWatch({ control, name: `${base}.payroll_entry_day_additional_retentions` as keyof IPayroll })
    const otherDed = useWatch({ control, name: `payroll_entries.${entryIndex}.other_deductions` as keyof IPayroll })

    const hoursSalary = getHoursSalary(Number(employeeId), entryDate as string | null, reports)

    // Only this day's total is written here; the entry-level deductions are an
    // aggregate owned by EntryAccordion (a day must not overwrite them).
    useEffect(() => {
        if (isCustom) return
        if (!generalInfo || !employeeId) return
        const { total } = calcDayTotal(
            Number(employeeId),
            entryDate as string | null,
            Number(otherDed),
            Number(additional),
            ((retentions as unknown as IPayrollEntryDayAdditionalRetention[]) ?? []).filter(r => !r._destroy),
            generalInfo,
            generalInfos,
            reports,
        )
        setValue(`${base}.total_amount`, total)
    }, [employeeId, entryDate, otherDed, additional, retentions, isCustom, generalInfo, generalInfos, reports])

    const dateLabel = entryDate ? dayjs(entryDate as string).format("ddd DD/MM/YYYY") : "New Day"

    return (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: "bold", flexGrow: 1 }}>{dateLabel}</Typography>
                {hoursSalary > 0 && (
                    <Chip
                        label={`Extra: RD$${formatMoney(hoursSalary)}`}
                        size="small"
                        color="warning"
                        variant="outlined"
                        sx={{ mr: 1 }}
                    />
                )}
                <Tooltip title={t("payrolls.form.removeDay")}>
                    <IconButton size="small" color="error" onClick={onRemove}>
                        <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr 1fr 1fr" }, gap: 1 }}>
                <Controller
                    key={`entry_date-${entryIndex}-${dayIndex}`}
                    control={control}
                    name={`${base}.entry_date` as keyof IPayroll}
                    render={({ field }) => (
                        <DatePicker
                            label="Date"
                            value={field.value ? dayjs(field.value as string) : null}
                            onChange={val => field.onChange(val?.toISOString() ?? null)}
                            format="DD-MM-YYYY"
                            slotProps={{ textField: { size: "small", fullWidth: true } }}
                        />
                    )}
                />

                <TextField
                    size="small"
                    label={t("payrolls.form.extraHoursSalary")}
                    value={formatMoney(hoursSalary)}
                    slotProps={{ input: { readOnly: true } }}
                    fullWidth
                />

                <Controller
                    key={`total_amount-${entryIndex}-${dayIndex}`}
                    control={control}
                    name={`${base}.total_amount` as keyof IPayroll}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            size="small"
                            label={t("common.total")}
                            type="number"
                            slotProps={{ input: { readOnly: !isCustom } }}
                            fullWidth
                        />
                    )}
                />

                <Controller
                    key={`additional_value-${entryIndex}-${dayIndex}`}
                    control={control}
                    name={`${base}.additional_value` as keyof IPayroll}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            size="small"
                            label={t("payrolls.form.additional")}
                            type="number"
                            fullWidth
                        />
                    )}
                />
            </Box>

            <Box sx={{ mt: 1 }}>
                <Controller
                    key={`is_custom-${entryIndex}-${dayIndex}`}
                    control={control}
                    name={`${base}.is_custom` as keyof IPayroll}
                    render={({ field: { value, ...field } }) => (
                        <FormControlLabel
                            {...field}
                            label={<Typography variant="caption">{t("payrolls.form.customTotal")}</Typography>}
                            control={<Checkbox size="small" checked={!!value} />}
                        />
                    )}
                />
            </Box>

            <LoanRetentionsAccordion
                control={control}
                entryIndex={entryIndex}
                dayIndex={dayIndex}
                generalInfo={generalInfo}
                generalInfos={generalInfos}
                getValues={getValues}
            />
        </Paper>
    )
}

// ─── EntryAccordion ───────────────────────────────────────────────────────────

interface EntryAccordionProps {
    control: Control<IPayroll>
    entryIndex: number
    employees: IEmployee[]
    generalInfo: IGeneralInfo | null
    generalInfos: IGeneralInfo[]
    reports: ISlimReport[]
    setValue: (name: string, value: unknown) => void
    getValues: () => IPayroll
    onRemove: () => void
}

function EntryAccordion({
    control, entryIndex, employees, generalInfo, generalInfos, reports, setValue, getValues, onRemove,
}: EntryAccordionProps) {
    const { t } = useTranslation()
    const { fields: dayFields, append: appendDay, remove: removeDay, update: updateDay } = useFieldArray({
        control,
        name: `payroll_entries.${entryIndex}.payroll_entry_days` as unknown as "payroll_entries",
    })

    const employeeId = useWatch({ control, name: `payroll_entries.${entryIndex}.employee_id` as keyof IPayroll })
    const otherDed = useWatch({ control, name: `payroll_entries.${entryIndex}.other_deductions` as keyof IPayroll })
    const days = useWatch({ control, name: `payroll_entries.${entryIndex}.payroll_entry_days` as keyof IPayroll })
    const totalDed = useWatch({ control, name: `payroll_entries.${entryIndex}.total_deductions` as keyof IPayroll })

    const selectedEmployee = employees.find(e => e.id === Number(employeeId)) ?? null

    // Aggregated across every live day of the entry, so no day can clobber it.
    useEffect(() => {
        if (!generalInfo || !employeeId) return
        setValue(`payroll_entries.${entryIndex}.total_deductions`, computeEntryTotalDeductions(
            (days as unknown as IPayrollEntryDay[]) ?? [],
            Number(employeeId),
            Number(otherDed),
            generalInfo,
            generalInfos,
            reports,
        ))
    }, [employeeId, otherDed, days, generalInfo, generalInfos, reports])

    // Salary stats derived from generalInfo + employee
    const infoEntry = generalInfo?.general_info_entries.find(e => e.employee_id === Number(employeeId)) ?? null
    const salary = infoEntry ? infoEntry.salary / 4.33 : 0
    const afp = salary * 0.0287
    const ars = salary * 0.0304

    const handleAddDay = () => {
        const newDay = new PayrollEntryDay()
        if (generalInfo && selectedEmployee) {
            const loanRetentions = Util.getEmployeeActiveLoanRetentions(selectedEmployee.id, generalInfo.id, generalInfos)
            newDay.payroll_entry_day_additional_retentions = loanRetentions.map(r => {
                const ret = new PayrollEntryDayAdditionalRetention()
                ret.general_info_entry_additional_retention_id = r.id
                ret.general_info_entry_additional_retention = r
                ret.is_active = true
                ret._destroy = false
                ret.id = 0
                return ret
            })
        }
        appendDay(newDay as unknown as IPayrollEntry)
    }

    const handleRemoveDay = (dayIndex: number) => {
        const day = getValues().payroll_entries[entryIndex].payroll_entry_days[dayIndex]
        if (day.id && day.id > 0) {
            updateDay(dayIndex, { ...day, _destroy: true } as unknown as IPayrollEntry)
        } else {
            removeDay(dayIndex)
        }
    }

    const entryName = selectedEmployee
        ? `${selectedEmployee.name} ${selectedEmployee.last_name}`
        : `Entry #${entryIndex + 1}`

    return (
        <Accordion variant="outlined" defaultExpanded sx={{ mb: 1 }}>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                    flexDirection: "row-reverse",
                    px: 2,
                    minHeight: 52,
                    "& .MuiAccordionSummary-content": { my: 0, alignItems: "center", gap: 1.5, ml: 1 },
                }}
            >
                <Typography variant="body1" sx={{ fontWeight: "bold", flexGrow: 1 }}>{entryName}</Typography>
                <Chip label={t("payrolls.form.daysCount", { count: (dayFields as unknown as IPayrollEntryDay[]).filter(d => !d._destroy).length })} size="small" />
                <Tooltip title={t("common.removeEntry")}>
                    <IconButton
                        size="small"
                        color="error"
                        sx={{ flexShrink: 0 }}
                        onClick={e => { e.stopPropagation(); onRemove() }}
                    >
                        <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </AccordionSummary>
            <AccordionDetails>
                {/* Employee + stats row */}
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr 1fr 1fr 1fr 1fr 1fr" }, gap: 1, mb: 2 }}>
                    <Controller
                        // Force remount when entryIndex changes (e.g., server
                        // re-sorts entries by FPC ID after Save & Continue).
                        // The accordion key (DB id) keeps the parent mounted
                        // so its expanded state is preserved, but RHF's
                        // Controller does not re-subscribe cleanly when its
                        // `name` prop changes — leaving the autocomplete
                        // showing the previous entry's value.
                        key={`employee_id-${entryIndex}`}
                        control={control}
                        name={`payroll_entries.${entryIndex}.employee_id` as keyof IPayroll}
                        render={({ field }) => (
                            <Autocomplete
                                size="small"
                                options={employees}
                                value={employees.find(e => e.id === Number(field.value)) ?? null}
                                isOptionEqualToValue={(op, v) => op.id === v.id}
                                getOptionLabel={e => `FPC-${String(e.fpc_id).padStart(4, "0")} ${e.name} ${e.last_name}`}
                                onChange={(_, data) => field.onChange(data?.id ?? 0)}
                                renderInput={params => <TextField {...params} label={t("common.employee")} />}
                            />
                        )}
                    />
                    <TextField size="small" label={t("payrolls.form.salary")} value={formatMoney(salary)} slotProps={{ input: { readOnly: true } }} />
                    <TextField size="small" label={t("payrolls.form.dailySalary")} value={formatMoney(infoEntry?.daily_salary ?? 0)} slotProps={{ input: { readOnly: true } }} />
                    <TextField size="small" label="AFP (2.87%)" value={formatMoney(afp)} slotProps={{ input: { readOnly: true } }} />
                    <TextField size="small" label="ARS (3.04%)" value={formatMoney(ars)} slotProps={{ input: { readOnly: true } }} />
                    <Controller
                        key={`other_deductions-${entryIndex}`}
                        control={control}
                        name={`payroll_entries.${entryIndex}.other_deductions` as keyof IPayroll}
                        render={({ field }) => (
                            <TextField {...field} size="small" label={t("payrolls.form.otherDed")} type="number" />
                        )}
                    />
                    <TextField size="small" label={t("payrolls.form.totalDed")} value={formatMoney(Number(totalDed ?? 0))} slotProps={{ input: { readOnly: true } }} />
                </Box>

                {/* Days */}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">{t("common.days")}</Typography>
                    <Button size="small" startIcon={<AddIcon />} onClick={handleAddDay}>{t("common.addDay")}</Button>
                </Box>

                {(dayFields as unknown as IPayrollEntryDay[]).map((dayField, dayIndex) => {
                    if (dayField._destroy) return null
                    const dayId = getValues().payroll_entries[entryIndex]?.payroll_entry_days?.[dayIndex]?.id
                    const dayKey = typeof dayId === "number" && dayId > 0 ? dayId : `new-${dayIndex}`
                    return (
                        <DayCard
                            key={dayKey}
                            control={control}
                            entryIndex={entryIndex}
                            dayIndex={dayIndex}
                            generalInfo={generalInfo}
                            generalInfos={generalInfos}
                            reports={reports}
                            setValue={setValue}
                            getValues={getValues}
                            onRemove={() => handleRemoveDay(dayIndex)}
                        />
                    )
                })}

                {(dayFields as unknown as IPayrollEntryDay[]).filter(d => !d._destroy).length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                        {t("payrolls.form.noDays")}
                    </Typography>
                )}
            </AccordionDetails>
        </Accordion>
    )
}

// ─── Main Form ────────────────────────────────────────────────────────────────

export default function PayrollForm() {
    const { is_edit, payroll_base, payroll: initialPayroll, general_infos, reports, back_params, errors: serverErrors } =
        usePage<Props>().props
    const { t } = useTranslation()

    const emptyPayroll = new Payroll()
    if (payroll_base) {
        emptyPayroll.payroll_name = payroll_base.payroll_name
        emptyPayroll.payroll_date = parseDateOnly(payroll_base.payroll_date)
    }
    const defaultValues: IPayroll = initialPayroll ?? emptyPayroll

    const { control, handleSubmit, setValue, getValues, reset } = useForm<IPayroll>({ defaultValues })

    const { fields: entryFields, append: appendEntry, remove: removeEntry, update: updateEntry } = useFieldArray({
        control,
        name: "payroll_entries",
    })

    const generalInfos = general_infos ?? []
    const reports_ = reports ?? []
    const deferredLoading = initialPayroll === undefined || general_infos === undefined || reports === undefined

    // Re-sync RHF state to server data on initial deferred load AND after
    // every successful save (Save & Continue triggers a deferred re-fetch
    // that bumps updated_at). Without this, tombstones for already-deleted
    // rows and id=0 placeholders for newly-created ones stay in the form
    // and get re-sent on the next save (causing RecordNotFound or
    // duplicate creates).
    const payrollKey = `${initialPayroll?.id}-${initialPayroll?.updated_at}`
    useEffect(() => {
        if (initialPayroll) reset(initialPayroll)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [payrollKey])

    // Watched values for derived state
    const generalInfoId = useWatch({ control, name: "general_info_id" })
    const generalInfo = generalInfos.find(g => g.id === Number(generalInfoId)) ?? null

    const backQueryString = new URLSearchParams(
        Object.fromEntries(Object.entries(back_params).filter(([, v]) => v != null)) as Record<string, string>
    ).toString()
    const backUrl = `/payrolls?${backQueryString}`

    const [submitting, setSubmitting] = useState(false)
    const [localErrors, setLocalErrors] = useState<Record<string, string[]>>({})

    useEffect(() => {
        if (serverErrors && Object.keys(serverErrors).length > 0) {
            setLocalErrors(serverErrors)
        }
    }, [serverErrors])

    const buildOnError = (originalUrl: string) =>
        (errs: Record<string, string | string[]>) => {
            window.history.replaceState(window.history.state, "", originalUrl)
            setLocalErrors(
                Object.fromEntries(
                    Object.entries(errs).map(([k, v]) => [k, Array.isArray(v) ? v : [v]])
                )
            )
        }

    const handleSave: SubmitHandler<IPayroll> = (data) => {
        setSubmitting(true)
        setLocalErrors({})
        const originalUrl = window.location.pathname + window.location.search
        const url = is_edit ? `/payrolls/${data.id}?${backQueryString}` : `/payrolls?${backQueryString}`
        const method = is_edit ? "patch" : "post"
        router[method](url, { payroll: data as AnyData }, {
            onError: buildOnError(originalUrl),
            onSuccess: !is_edit ? () => { sessionStorage.removeItem('scroll_payrolls') } : undefined,
            onFinish: () => setSubmitting(false),
        })
    }

    const handleSaveAndContinue: SubmitHandler<IPayroll> = (data) => {
        setSubmitting(true)
        setLocalErrors({})
        const originalUrl = window.location.pathname + window.location.search
        if (is_edit) {
            router.patch(`/payrolls/${data.id}?${backQueryString}`, { payroll: data as AnyData, stay: true }, {
                only: ["payroll"],
                preserveState: true,
                preserveScroll: true,
                onError: buildOnError(originalUrl),
                onFinish: () => setSubmitting(false),
            })
        } else {
            router.post(`/payrolls?${backQueryString}`, { payroll: data as AnyData, stay: true }, {
                preserveScroll: true,
                onError: buildOnError(originalUrl),
                onFinish: () => setSubmitting(false),
            })
        }
    }

    const handleDownload = () => {
        if (is_edit && initialPayroll?.id) {
            window.location.href = `/payrolls/${initialPayroll.id}/download_excel`
        }
    }

    const handleAddEntry = () => {
        appendEntry(new PayrollEntry() as unknown as IPayrollEntry)
    }

    const handleRemoveEntry = (index: number) => {
        const entry = getValues().payroll_entries[index]
        if (entry.id && entry.id > 0) {
            updateEntry(index, { ...entry, _destroy: true } as unknown as IPayrollEntry)
        } else {
            removeEntry(index)
        }
    }

    const title = is_edit
        ? (payroll_base?.payroll_name ?? t("payrolls.form.editTitle"))
        : t("payrolls.form.newTitle")

    return (
        <>
            <Head title={title} />

            <Box
                sx={{
                    bgcolor: "background.paper",
                    borderBottom: 1,
                    borderColor: "divider",
                }}
            >
                <Container maxWidth="xl">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
                        <Tooltip title={t("common.back")}>
                            <IconButton LinkComponent={Link} href={backUrl} size="small">
                                <ArrowBackIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", sm: "block" } }} />
                        <Typography
                            variant="subtitle1"
                            noWrap
                            sx={{ fontWeight: "bold", flexGrow: 1, display: { xs: "none", sm: "block" } }}
                        >
                            {title}
                        </Typography>
                        <Box sx={{ flexGrow: 1, display: { xs: "block", sm: "none" } }} />

                        {is_edit && (
                            <>
                                <Tooltip title={t("common.downloadExcel")}>
                                    <span>
                                        <IconButton
                                            color="success"
                                            size="small"
                                            onClick={handleDownload}
                                            disabled={submitting}
                                            sx={{ display: { xs: "inline-flex", sm: "none" } }}
                                        >
                                            <DownloadIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Button
                                    variant="outlined"
                                    color="success"
                                    size="small"
                                    startIcon={<DownloadIcon />}
                                    onClick={handleDownload}
                                    disabled={submitting}
                                    sx={{ display: { xs: "none", sm: "inline-flex" } }}
                                >
                                    {t("common.excel")}
                                </Button>
                            </>
                        )}

                        <Button color="inherit" LinkComponent={Link} href={backUrl} size="small" disabled={deferredLoading || submitting}>
                            {t("common.cancel")}
                        </Button>
                        <Button variant="outlined" size="small" onClick={handleSubmit(handleSaveAndContinue)} disabled={deferredLoading || submitting}>
                            {t("common.saveAndContinue")}
                        </Button>
                        <Button variant="contained" size="small" onClick={handleSubmit(handleSave)} disabled={deferredLoading || submitting}>
                            {submitting ? t("common.saving") : t("common.save")}
                        </Button>
                    </Box>
                </Container>
            </Box>

            <Container maxWidth="xl" sx={{ py: 3 }}>
                {Object.keys(localErrors).length > 0 && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {formatErrorMessages(t, localErrors).join(" · ")}
                    </Alert>
                )}

                {/* Basic fields */}
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr 2fr" }, gap: 2, mb: 3 }}>
                    <Controller
                        control={control}
                        name="payroll_name"
                        render={({ field }) => (
                            <TextField
                                {...field}
                                label={t("common.name")}
                                size="small"
                                fullWidth
                                error={!!localErrors.payroll_name}
                                helperText={translateErrorMessage(t, localErrors.payroll_name)}
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="payroll_date"
                        render={({ field }) => (
                            <DatePicker
                                label={t("common.date")}
                                value={field.value ? dayjs(field.value as unknown as string) : null}
                                onChange={val => field.onChange(val?.toISOString() ?? null)}
                                format="DD-MM-YYYY"
                                slotProps={{
                                    textField: {
                                        size: "small",
                                        fullWidth: true,
                                        error: !!localErrors.payroll_date,
                                        helperText: translateErrorMessage(t, localErrors.payroll_date),
                                    },
                                }}
                            />
                        )}
                    />

                    <Deferred data={["general_infos"]} fallback={<Skeleton variant="rounded" height={40} />}>
                        <Controller
                            control={control}
                            name="general_info_id"
                            render={({ field }) => (
                                <Autocomplete
                                    size="small"
                                    options={generalInfos}
                                    value={generalInfos.find(g => g.id === Number(field.value)) ?? null}
                                    isOptionEqualToValue={(op, v) => op.id === v.id}
                                    getOptionLabel={g => g.name}
                                    onChange={(_, data) => {
                                        field.onChange(data?.id ?? 0)
                                        setValue("general_info", data ?? null)
                                    }}
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            label={t("common.generalInfo")}
                                            error={!!localErrors.general_info}
                                            helperText={translateErrorMessage(t, localErrors.general_info)}
                                        />
                                    )}
                                />
                            )}
                        />
                    </Deferred>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* Entries */}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>{t("common.entries")}</Typography>
                    <Deferred data={["general_infos", "reports"]} fallback={
                        <Button variant="outlined" startIcon={<AddIcon />} disabled>{t("common.addEntry")}</Button>
                    }>
                        <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddEntry}>{t("common.addEntry")}</Button>
                    </Deferred>
                </Box>

                <Deferred
                    data={["general_infos", "reports"]}
                    fallback={
                        <>
                            <Skeleton variant="rounded" height={56} sx={{ mb: 1 }} />
                            <Skeleton variant="rounded" height={56} sx={{ mb: 1 }} />
                            <Skeleton variant="rounded" height={56} />
                        </>
                    }
                >
                    {(entryFields as unknown as IPayrollEntry[]).map((entryField, entryIndex) => {
                        if (entryField._destroy) return null
                        const entryId = getValues().payroll_entries[entryIndex]?.id
                        const entryKey = typeof entryId === "number" && entryId > 0 ? entryId : `new-${entryIndex}`
                        return (
                            <EntryAccordion
                                key={entryKey}
                                control={control}
                                entryIndex={entryIndex}
                                employees={general_infos
                                    ? ((generalInfo?.general_info_entries.map(e => e.employee).filter(Boolean) as IEmployee[]) ?? [])
                                        .slice()
                                        .sort((a, b) => a.fpc_id - b.fpc_id)
                                    : []
                                }
                                generalInfo={generalInfo}
                                generalInfos={generalInfos}
                                reports={reports_}
                                setValue={setValue as unknown as (name: string, value: unknown) => void}
                                getValues={getValues as unknown as () => IPayroll}
                                onRemove={() => handleRemoveEntry(entryIndex)}
                            />
                        )
                    })}
                    {(entryFields as unknown as IPayrollEntry[]).filter(e => !e._destroy).length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                            {t("payrolls.form.noEntries")}
                        </Typography>
                    )}
                </Deferred>
            </Container>
        </>
    )
}
